import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowUp,
  Check,
  ChevronDown,
  Database,
  FileText,
  LayoutDashboard,
  Loader2,
  MessagesSquare,
  Pencil,
  Plug,
  RotateCcw,
  ScrollText,
  Sparkles,
  Square,
  Target,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";

import {
  agentRunStreamURL,
  APIError,
  approveMissionPlan,
  cancelMission,
  fetchAgentFile,
  cancelAgentRun,
  getAgentRun,
  listDecisions,
  postAgentMessage,
  replyMissionPlan,
  type AgentFileRef,
  type AgentMessageView,
  type AgentRunStatus,
  type PendingDecision,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import { InlinePlanReview, MissionResultCard, OngoingMissions, useSessionMissions } from "@/components/MissionInbox";
import { FileCard, FileLink, FileLinkContext, FilePreviewBody, parseVolumePath, remarkWorkspaceFiles, type WorkspaceFileRef } from "@/components/FilePreview";

// nodeText flattens react-markdown inline children to a plain string (for matching
// a file path inside inline code).
function nodeText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(nodeText).join("");
  return "";
}

export type AgentChatHandle = {
  newChat: () => void;
  loadConversation: (conversationId: string, runId: string) => void;
};

// A run is "active" (worth streaming/polling) until it reaches a terminal state.
const isActive = (s: string) => s === "runnable" || s === "running" || s === "awaiting_human";

// Markdown tuned for a reading-width conversation column. react-markdown tolerates
// the partial markdown that arrives mid-stream, so this renders while typing.
const CHAT_MD: Components = {
  p: (p) => <p className="my-2.5 first:mt-0 last:mb-0">{p.children}</p>,
  ul: (p) => <ul className="my-2.5 list-disc space-y-1 pl-5">{p.children}</ul>,
  ol: (p) => <ol className="my-2.5 list-decimal space-y-1 pl-5">{p.children}</ol>,
  li: (p) => <li className="leading-normal">{p.children}</li>,
  strong: (p) => <strong className="font-semibold">{p.children}</strong>,
  em: (p) => <em>{p.children}</em>,
  a: (p) => {
    const f = p.href ? parseVolumePath(p.href) : null;
    if (f) return <FileLink file={f}>{p.children}</FileLink>;
    return (
      <a href={p.href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
        {p.children}
      </a>
    );
  },
  h1: (p) => <h2 className="mb-2 mt-4 text-xl font-semibold first:mt-0">{p.children}</h2>,
  h2: (p) => <h3 className="mb-2 mt-4 text-lg font-semibold first:mt-0">{p.children}</h3>,
  h3: (p) => <h4 className="mb-1.5 mt-3 text-base font-semibold first:mt-0">{p.children}</h4>,
  blockquote: (p) => (
    <blockquote className="my-2.5 border-l-2 border-border pl-3 italic text-muted-foreground">{p.children}</blockquote>
  ),
  code: (p: { children?: ReactNode; className?: string }) => {
    if (p.className) return <code className={p.className}>{p.children}</code>;
    const f = parseVolumePath(nodeText(p.children)); // inline `state/files/x.md` → clickable
    if (f) return <FileLink file={f}>{p.children}</FileLink>;
    return <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]">{p.children}</code>;
  },
  pre: (p) => (
    <pre className="my-2.5 overflow-auto rounded-lg border bg-muted/60 p-3 font-mono text-sm leading-relaxed">
      {p.children}
    </pre>
  ),
  // Markdown images render (e.g. an agent-generated chart as a data: URI or URL).
  // Only markdown ![]() images reach here — raw HTML isn't parsed — so this is safe.
  img: (p) => <img src={p.src} alt={p.alt ?? ""} className="my-2.5 max-w-full rounded-lg border" loading="lazy" />,
  table: (p) => (
    <div className="my-2.5 overflow-auto">
      <table className="w-full border-collapse text-sm">{p.children}</table>
    </div>
  ),
  th: (p) => <th className="border border-border bg-muted/50 px-2 py-1 text-left font-semibold">{p.children}</th>,
  td: (p) => <td className="border border-border px-2 py-1">{p.children}</td>,
  hr: () => <hr className="my-4 border-border" />,
};

// While a message is still streaming, the trailing block is often half-typed: an
// unclosed ``` fence renders as a plain paragraph until the closing fence arrives,
// then snaps into a <pre> box — a jarring layout jump. Balancing the open fence on
// the draft path keeps the code block rendered as a code block the whole time, so
// the layout stays stable as tokens arrive. Only applied to the live draft;
// finished messages render verbatim.
function stabilizeStreamingMarkdown(md: string): string {
  const fences = md.match(/^[ \t]*```/gm);
  if (fences && fences.length % 2 === 1) {
    return md + (md.endsWith("\n") ? "" : "\n") + "```";
  }
  return md;
}

function ChatMarkdown({ content, streaming }: { content: string; streaming?: boolean }) {
  const md = streaming ? stabilizeStreamingMarkdown(content) : content;
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkWorkspaceFiles]} components={CHAT_MD}>
      {md}
    </ReactMarkdown>
  );
}

// AssetImage renders a file artifact a turn produced. The bytes are fetched
// (authenticated) from the deployment's volume and shown via a blob URL — images
// inline, other files as a download link. Revokes the object URL on unmount.
function AssetImage({ projectId, file }: { projectId: string; file: AgentFileRef }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let obj = "";
    let live = true;
    fetchAgentFile(projectId, file.path)
      .then((blob) => {
        if (!live) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [projectId, file.path]);
  if (failed) return <div className="text-xs text-muted-foreground">Couldn't load {file.name}</div>;
  if (!url) return <div className="h-40 w-full max-w-sm animate-pulse rounded-lg border bg-muted/40" />;
  if (file.mime.startsWith("image/")) {
    return <img src={url} alt={file.name} className="max-w-full rounded-lg border" loading="lazy" />;
  }
  return (
    <a href={url} download={file.name} className="text-sm text-primary underline underline-offset-2">
      {file.name}
    </a>
  );
}

// A file/view opened in the right-side viewer panel. A "view" is a generative-UI
// page (served from the isolated origin, rendered in a sandboxed iframe); a "file"
// is a workspace file (markdown/text/image) read through the authenticated endpoint.
type PanelItem = { kind: "view"; url: string; name: string } | { kind: "file"; file: WorkspaceFileRef };

// ViewOpenContext carries the "open this view in the panel" callback down to the
// memoized Turn rows (parallel to FileLinkContext for workspace files).
const ViewOpenContext = createContext<((file: AgentFileRef) => void) | null>(null);

// ViewCard is the compact in-chat affordance for a generated view: a click opens
// it in the right panel (vs a heavy inline iframe per message).
function ViewCard({ file }: { file: AgentFileRef }) {
  const open = useContext(ViewOpenContext);
  return (
    <button
      type="button"
      onClick={() => open?.(file)}
      className="flex w-full max-w-sm items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-left transition hover:border-primary/40 hover:bg-primary/5"
      title={`Open ${file.name || "view"}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <LayoutDashboard className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{file.name || "Generated view"}</span>
        <span className="block text-xs text-muted-foreground">Interactive view · click to open</span>
      </span>
    </button>
  );
}

// FileViewerPanel is the right-hand column that renders the tapped file or view.
// A view is served from a SEPARATE isolated origin (no shared cookies) inside a
// `sandbox="allow-scripts"` iframe — an opaque origin that can run JS to render but
// can't reach the dashboard, its cookies, or (via the origin's `connect-src 'none'`
// CSP) the network. We never proxy a view through the authenticated file endpoint.
function FileViewerPanel({
  projectId,
  item,
  onClose,
}: {
  projectId: string;
  item: PanelItem;
  onClose: () => void;
}) {
  const isView = item.kind === "view";
  const name = isView ? item.name || "Generated view" : item.file.name;
  return (
    <aside className="fixed inset-x-0 bottom-0 top-14 z-40 flex w-full flex-col border-l bg-background sm:static sm:inset-auto sm:top-auto sm:z-auto sm:min-w-0 sm:flex-1">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        {isView ? (
          <LayoutDashboard className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={name}>
          {name}
        </span>
        {isView && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            title="Open in a new tab"
          >
            Open ↗
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {isView ? (
        <iframe
          src={item.url}
          title={name}
          sandbox="allow-scripts"
          className="min-h-0 flex-1 w-full bg-background"
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <FilePreviewBody projectId={projectId} file={item.file} />
        </div>
      )}
    </aside>
  );
}

// One conversation turn. User messages are a right-aligned bubble; assistant prose
// is full-width markdown in the same modern sans as the user's messages. Memoized so
// finished turns don't re-render on every streamed token.
const Turn = memo(function Turn({
  role,
  text,
  files,
  streaming,
  projectId,
  onRetry,
  missionId,
}: {
  role: string;
  text: string;
  files?: AgentFileRef[];
  streaming?: boolean;
  projectId: string;
  onRetry?: (text: string) => void;
  missionId?: string;
}) {
  if (role === "user") {
    return (
      <div className="group flex items-center justify-end gap-1.5">
        {onRetry && (
          <button
            className="opacity-0 transition-opacity group-hover:opacity-100"
            title="Send again"
            onClick={() => onRetry(text)}
          >
            <RotateCcw className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        )}
        <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-secondary px-4 py-2.5 text-[15px] leading-relaxed text-secondary-foreground">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="text-[15px] leading-relaxed text-foreground">
      {missionId && <MissionResultCard projectId={projectId} missionId={missionId} />}
      <ChatMarkdown content={text} streaming={streaming} />
      {files && files.length > 0 && (
        <div className="mt-3 space-y-3">
          {files.map((f, i) =>
            f.kind === "view" && f.url ? (
              <ViewCard key={i} file={f} />
            ) : f.mime.startsWith("image/") ? (
              <AssetImage key={i} projectId={projectId} file={f} />
            ) : (
              <FileCard key={i} file={{ volume: "state", path: f.path, name: f.name }} />
            ),
          )}
        </div>
      )}
    </div>
  );
});

// History is memoized on the messages array identity, so streaming a draft (which
// changes only `draft`, not `messages`) never re-renders past turns.
const History = memo(function History({
  messages,
  projectId,
  onRetry,
}: {
  messages: AgentMessageView[];
  projectId: string;
  onRetry?: (text: string) => void;
}) {
  return (
    <>
      {messages.map((m, i) => (
        <Turn key={i} role={m.role} text={m.text} files={m.files} projectId={projectId} onRetry={onRetry} missionId={m.mission_id} />
      ))}
    </>
  );
});

function Thinking({ label }: { label?: string }) {
  return (
    <div className="flex w-fit items-center gap-2 py-1">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.3s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.15s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" />
      </div>
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
    </div>
  );
}

// Friendly label for the tool the agent is currently running (raw MCP names are
// opaque to a non-technical owner). Falls back to a de-underscored name.
const TOOL_LABELS: Record<string, string> = {
  execute_code: "Running code",
  read_file: "Reading a file",
  write_file: "Saving a file",
  list_directory: "Looking through files",
  ask_human: "Asking a human",
  get_answer: "Checking for an answer",
  check_approval: "Checking for approval",
};
function prettyTool(name: string): string {
  return TOOL_LABELS[name] ?? `Running ${name.replace(/[_-]+/g, " ")}`;
}

// Live "the agent is doing something under the hood" row, shown between text
// segments while a tool call runs server-side (mirrors Claude's tool progress).
function ToolRunning({ name }: { name: string }) {
  return (
    <div className="flex w-fit items-center gap-2 py-1 text-sm text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>{prettyTool(name)}…</span>
    </div>
  );
}

// When a run parks on ask_human / an approval HOLD, this replaces the old static
// amber box with an in-progress pill (spinner + "Waiting on a human…") that
// expands to show the actual escalated question/approval. It resolves on its own
// once a human answers anywhere (here, Governance, or Telegram/Slack).
function WaitingPill({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  useEffect(() => {
    let live = true;
    listDecisions(projectId)
      .then((r) => {
        if (live) setDecision(r.decisions[0] ?? null);
      })
      .catch(() => {
        /* generic copy is the fallback */
      });
    return () => {
      live = false;
    };
  }, [projectId]);
  const isQuestion = decision?.kind === "question";
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex w-fit items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/50"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
        Waiting on a human…
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="rounded-xl border bg-card p-3 text-sm">
          {decision ? (
            isQuestion ? (
              <>
                <div className="mb-1 font-medium text-foreground">The agent asked a question</div>
                <div className="whitespace-pre-wrap text-foreground/90">{decision.question || decision.reason}</div>
              </>
            ) : (
              <>
                <div className="mb-1 font-medium text-foreground">Approval needed: {decision.tool}</div>
                {decision.reason && <div className="text-foreground/90">{decision.reason}</div>}
              </>
            )
          ) : (
            <div className="text-foreground/90">The agent escalated a question or approval.</div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Answer it here, in Settings → Governance, or your connected Telegram/Slack — the reply appears here
            automatically. You can leave; it resumes on its own.
          </p>
        </div>
      )}
    </div>
  );
}

// Brand marks for the empty-state channel arc — the channels an agent can
// actually be reached on (the platform chat + Telegram + Slack).
function TelegramLogo() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden>
      <defs>
        <linearGradient id="cb-tg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#37BBFE" />
          <stop offset="1" stopColor="#007DBB" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="12" fill="url(#cb-tg)" />
      <path
        fill="#fff"
        d="M17.78 7.3l-2.02 9.52c-.15.67-.55.83-1.12.52l-3.06-2.26-1.48 1.42c-.16.16-.3.3-.62.3l.22-3.12 5.67-5.12c.25-.22-.05-.34-.38-.13L7.97 13.4 4.9 12.44c-.66-.21-.68-.66.14-.98l11.97-4.61c.55-.2 1.03.13.77 1.04z"
      />
    </svg>
  );
}
function SlackLogo() {
  return (
    <svg viewBox="0 0 122.8 122.8" className="h-6 w-6" aria-hidden>
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#E01E5A" />
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0" />
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D" />
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ECB22E" />
    </svg>
  );
}

// ChannelBadge is one floating logo bubble in the empty-state arc.
function ChannelBadge({ className, children }: { className: string; children: ReactNode }) {
  return (
    <div
      className={`absolute flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg ring-1 ring-black/5 ${className}`}
    >
      {children}
    </div>
  );
}

// Capability is one honest "what this agent can do" card on the empty-chat state.
function Capability({ icon: Icon, title, desc }: { icon: LucideIcon; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border bg-card p-4 text-left">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}

// AgentChatPanel is the inline conversation with the PLATFORM-RUN agent: messages
// run server-side (it executes its own tools), stream back live over SSE, and —
// when the agent escalates with ask_human or hits an approval HOLD — the run
// parks and resumes on its own once a human answers, with the reply appearing
// here. Conversation + run id persist per project (reopen days later → reloads).
//
// Resilience: SSE drives live token streaming; a low-frequency poll of the run
// (the durable source of truth) reconciles status — crucially when the run
// resumes OUT OF BAND (answered in Notifications/Telegram) while no stream is
// open, so the "waiting for a human" state always clears. Tokens are coalesced to
// one render per animation frame to keep streaming smooth.
// Verbatim copies of the agent's backend acknowledgments (RecordPlanDecision) so the
// optimistic echo matches what the server persists (and shows on reload).
const ACK_APPROVE = "👍 Approved — I'll start working through the plan now and keep you posted here as I go.";
const ACK_REVISE = "Got it — I'll revise the plan and bring it back for your approval.";
const ACK_DISCARD = "Okay — I've discarded this mission.";

export const AgentChatPanel = forwardRef<AgentChatHandle, { projectId: string; agentName: string }>(
  function AgentChatPanel({ projectId, agentName }, ref) {
    const toast = useToast();
    const storeKey = `cb-agent-conv:${projectId}`;
    const [messages, setMessages] = useState<AgentMessageView[]>([]);
    const [draft, setDraft] = useState("");
    const [input, setInput] = useState("");
    const [status, setStatus] = useState<AgentRunStatus | "">("");
    const [busy, setBusy] = useState(false);
    // Mission mode (like plan mode): when on, the next message DETERMINISTICALLY
    // starts a mission instead of relying on the agent's task-vs-mission judgment.
    const [missionMode, setMissionMode] = useState(false);
    // planBusy: a BUTTON decision (Approve/Discard) is in flight → spin/disable the
    // buttons. replyBusy: a TYPED reply is being read by the model → show thinking dots
    // in the reply area (like a normal chat turn), NOT a button spinner. planBusyRef is
    // the synchronous lock across BOTH paths (prevents Enter-then-click double-fire).
    const [planBusy, setPlanBusy] = useState(false);
    const [replyBusy, setReplyBusy] = useState(false);
    const planBusyRef = useRef(false);
    // The mission currently being re-planned after a revise. While set, the dock shows
    // it as a passive "Revising…" card (not a steerable RunningCard) so the brief
    // awaiting→ticking→awaiting hop never reads as "it ran without my approval".
    const [revisingId, setRevisingId] = useState<string | null>(null);
    // The tool the agent is currently running this turn (from the tool_call
    // delta), shown as a live "Running X…" progress row. Cleared when the next
    // text arrives, the run parks/finishes, or the draft resets.
    const [activeTool, setActiveTool] = useState("");
    // Coarse activity label for the waiting indicator (from the run's "phase"
    // deltas): "preparing" (connecting to the agent) → "thinking" (model turn).
    // Makes a long pause legible instead of an opaque row of dots.
    const [phase, setPhase] = useState("");
    // True only while the model is actively streaming text THIS turn. Gates the
    // waiting indicator on real streaming (not on whether a draft exists): the
    // draft accumulates across a tool loop, so between steps it's non-empty while
    // the model is thinking — we still want the "Thinking…" row then, below it.
    const [streaming, setStreaming] = useState(false);
    // Reactive mirror of convRef.current so the inline MissionInbox (scoped to the
    // current chat session) re-filters when the conversation switches.
    const [convId, setConvId] = useState("");

    const convRef = useRef<string>("");
    const runIdRef = useRef<string>("");
    const statusRef = useRef<AgentRunStatus | "">("");
    const esRef = useRef<EventSource | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const pinnedRef = useRef(true);
    const draftBufRef = useRef("");
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
      statusRef.current = status;
    }, [status]);

    const resetDraft = useCallback(() => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      draftBufRef.current = "";
      setDraft("");
      setActiveTool("");
      setPhase("");
      setStreaming(false);
    }, []);

    const persist = useCallback(
      (runId: string) => {
        runIdRef.current = runId;
        try {
          localStorage.setItem(storeKey, JSON.stringify({ conversationId: convRef.current, runId }));
        } catch {
          /* ignore quota / private mode */
        }
      },
      [storeKey],
    );

    const openStream = useCallback(
      (runId: string) => {
        esRef.current?.close();
        const es = new EventSource(agentRunStreamURL(projectId, runId), { withCredentials: true });
        esRef.current = es;
        es.addEventListener("snapshot", (e) => {
          try {
            const v = JSON.parse((e as MessageEvent).data);
            setMessages(v.messages ?? []);
            setStatus(v.status ?? "");
            // Only clear the draft when the run is FINISHED (the snapshot then
            // carries the persisted final message). Mid-run, a (re)opened stream's
            // snapshot must NOT wipe the live draft — deltas keep appending and the
            // `done` handler does the atomic swap. Clearing it here was the flicker.
            if (!isActive(v.status)) {
              resetDraft();
              setBusy(false);
            }
          } catch {
            /* ignore */
          }
        });
        es.addEventListener("delta", (e) => {
          try {
            const d = JSON.parse((e as MessageEvent).data);
            if (d.text) {
              // Model is streaming this turn → the tool row + waiting dots clear and
              // the live text shows instead.
              setActiveTool("");
              setStreaming(true);
              draftBufRef.current += d.text;
              if (rafRef.current == null) {
                rafRef.current = requestAnimationFrame(() => {
                  rafRef.current = null;
                  const chunk = draftBufRef.current;
                  draftBufRef.current = "";
                  if (chunk) setDraft((prev) => prev + chunk);
                });
              }
            }
            if (d.tool_call) {
              setActiveTool(d.tool_call);
              setStreaming(false);
            }
            if (d.phase) {
              setPhase(d.phase);
              // A new model turn — we're WAITING for its first output (not streaming
              // yet). Clear the prior "Running X…" row so the waiting dots show even
              // though the accumulated draft from earlier steps is still on screen.
              setStreaming(false);
              if (d.phase === "thinking") setActiveTool("");
            }
            if (d.status) {
              setStatus(d.status);
              if (!isActive(d.status)) setBusy(false);
              if (d.status === "awaiting_human") {
                setActiveTool("");
                setStreaming(false);
              }
            }
            if (d.done) {
              setActiveTool("");
              setStreaming(false);
              setBusy(false);
              es.close();
              // Reconcile to the final persisted state: the live draft is
              // text-only, so any image artifacts on the last message only
              // appear after we re-read the run.
              void getAgentRun(projectId, runId)
                .then((v) => {
                  setMessages(v.messages ?? []);
                  setStatus(v.status ?? "");
                  resetDraft();
                })
                .catch(() => {
                  /* the poll/next open reconciles */
                });
            }
          } catch {
            /* ignore */
          }
        });
        es.onerror = () => {
          es.close(); // run is durable; the poll keeps the UI converging
          setActiveTool(""); // don't leave a "Running…" row frozen on a dropped stream
        };
      },
      [projectId, resetDraft],
    );

    // Safety-net poll: while active, reconcile from the durable record. Acts only
    // on a status transition (or terminal) so it never clobbers the live draft.
    useEffect(() => {
      if (!runIdRef.current || !isActive(status)) return;
      const id = setInterval(async () => {
        const runId = runIdRef.current;
        if (!runId) return;
        try {
          const v = await getAgentRun(projectId, runId);
          if (v.status === statusRef.current && isActive(v.status)) {
            // Status unchanged — but if the live stream dropped (e.g. a network
            // blip during a long tool call), reopen it so tokens resume and the
            // stale "Running…" row clears.
            if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
              setActiveTool("");
              openStream(runId);
            }
            return;
          }
          setMessages(v.messages ?? []);
          setStatus(v.status);
          if (!isActive(v.status)) {
            // Terminal: the durable record now holds the final message — safe to
            // clear the live draft.
            resetDraft();
            setBusy(false);
          } else {
            // Active transition (e.g. runnable→running on the first message): keep
            // the live draft and only (re)open the stream if it isn't already
            // flowing. Reopening emits a fresh snapshot that would wipe the draft —
            // that redundant reopen→snapshot→reset chain was the first-message flicker.
            setBusy(true);
            if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) openStream(runId);
          }
        } catch {
          /* transient */
        }
      }, 3500);
      return () => clearInterval(id);
    }, [status, projectId, openStream, resetDraft]);

    // Idle refresh: while the run is NOT active, a mission this conversation
    // started can post its result as an assistant message out-of-band. Poll the
    // run at a low cadence and append anything new so the reply shows without a
    // reload. Guarded on !busy so it never clobbers a live draft/turn.
    useEffect(() => {
      if (busy || isActive(status) || !runIdRef.current) return;
      const id = setInterval(async () => {
        const runId = runIdRef.current;
        if (!runId) return;
        try {
          const v = await getAgentRun(projectId, runId);
          setMessages((prev) => ((v.messages?.length ?? 0) > prev.length ? v.messages : prev));
        } catch {
          /* transient */
        }
      }, 10000);
      return () => clearInterval(id);
    }, [busy, status, projectId]);

    // On mount: resume a persisted conversation, else start a fresh id.
    useEffect(() => {
      let live = true;
      try {
        const raw = localStorage.getItem(storeKey);
        if (raw) {
          const { conversationId, runId } = JSON.parse(raw);
          if (conversationId) {
            convRef.current = conversationId;
            setConvId(conversationId);
          }
          if (runId) {
            runIdRef.current = runId;
            getAgentRun(projectId, runId)
              .then((v) => {
                if (!live) return;
                setMessages(v.messages ?? []);
                setStatus(v.status ?? "");
                if (isActive(v.status)) {
                  setBusy(true);
                  openStream(runId);
                }
              })
              .catch(() => {
                /* reaped/unknown — keep the id, start fresh visually */
              });
          }
        }
      } catch {
        /* ignore */
      }
      if (!convRef.current) convRef.current = crypto.randomUUID();
      setConvId(convRef.current);
      return () => {
        live = false;
        esRef.current?.close();
        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      };
    }, [projectId, storeKey, openStream]);

    // Keep pinned to the bottom unless the user scrolled up to read history (rAF so
    // it lands after paint). Also re-runs when the waiting/tool indicator toggles
    // (activeTool/streaming) so the row removing + re-adding between steps follows
    // the bottom when pinned — and does NOT move the view when the user is reading
    // up (pinnedRef guards that).
    useEffect(() => {
      if (!pinnedRef.current) return;
      const el = scrollRef.current;
      if (!el) return;
      const r = requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
      return () => cancelAnimationFrame(r);
    }, [messages, draft, status, busy, activeTool, streaming]);

    const growComposer = () => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    };

    // Missions for this chat session: ongoing ones pin in a sticky dock (always
    // visible), finished ones stay inline in the transcript.
    const sessionMissions = useSessionMissions(projectId, convId);
    // A plan waiting for the owner's go-ahead. Its plan renders inline in the
    // transcript (InlinePlanReview) and its actions live in the composer's plan bar;
    // while one is pending, a typed message REVISES the plan instead of chatting.
    const awaitingMission = sessionMissions.awaiting[0] ?? null;

    // Once the revised plan is back awaiting approval, stop treating it as "revising"
    // (so a later, real execution shows the normal steerable RunningCard).
    useEffect(() => {
      if (revisingId && awaitingMission?.id === revisingId) setRevisingId(null);
    }, [awaitingMission, revisingId]);

    // When a plan becomes pending, put the cursor in the message box so Enter always
    // sends to the plan reply — never accidentally activates a focused Approve button.
    useEffect(() => {
      if (awaitingMission) textareaRef.current?.focus();
    }, [awaitingMission?.id]);

    // A mission's status hops awaiting→ticking→awaiting (revise) or →active (approve)
    // server-side; the 6s background poll is too slow to feel responsive, so after a
    // decision we burst-poll to surface the new state (running card / revised plan) fast.
    const burstReload = useCallback(() => {
      [1200, 2800, 5000, 8000].forEach((ms) => window.setTimeout(() => sessionMissions.reload(), ms));
    }, [sessionMissions]);

    // decidePlan runs an EXPLICIT button decision (Approve / Discard) as a chat
    // exchange — the user's line, then the agent's acknowledgment. The server also
    // persists BOTH sides (RecordPlanDecision) so the exchange survives a reload.
    // Buttons act directly (no model needed).
    const decidePlan = useCallback(
      async (action: "approve" | "discard", userText: string) => {
        if (!awaitingMission || planBusyRef.current) return;
        const mid = awaitingMission.id;
        planBusyRef.current = true;
        setPlanBusy(true);
        setMessages((prev) => [...prev, { role: "user", text: userText }]);
        try {
          if (action === "approve") {
            await approveMissionPlan(projectId, mid);
            setMessages((prev) => [...prev, { role: "assistant", text: ACK_APPROVE }]);
          } else {
            await cancelMission(projectId, mid);
            setMessages((prev) => [...prev, { role: "assistant", text: ACK_DISCARD }]);
          }
          if (action === "approve") burstReload();
          sessionMissions.reload();
        } catch (e) {
          setMessages((prev) => [...prev, { role: "assistant", text: "_(couldn't do that — please try again)_" }]);
          toast.error(humanizeError(e, "Couldn't update the plan"));
          sessionMissions.reload(); // self-correct if the action actually landed
        } finally {
          planBusyRef.current = false;
          setPlanBusy(false);
        }
      },
      [awaitingMission, burstReload, projectId, sessionMissions, toast],
    );

    // replyToPlan handles a TYPED reply to a pending plan: a small model on the server
    // reads the intent (approve / revise / discard) and runs it; we show the user's line
    // then the matching acknowledgment. "revise" re-plans with the reply and re-presents.
    const replyToPlan = useCallback(
      async (text: string) => {
        if (!awaitingMission || planBusyRef.current) return;
        const mid = awaitingMission.id;
        planBusyRef.current = true;
        setReplyBusy(true); // thinking dots in the reply area — NOT a button spinner
        setMessages((prev) => [...prev, { role: "user", text }]);
        try {
          const { action } = await replyMissionPlan(projectId, mid, text);
          if (action === "approve") {
            setMessages((prev) => [...prev, { role: "assistant", text: ACK_APPROVE }]);
          } else if (action === "discard") {
            setMessages((prev) => [...prev, { role: "assistant", text: ACK_DISCARD }]);
          } else {
            setRevisingId(mid);
            setMessages((prev) => [...prev, { role: "assistant", text: ACK_REVISE }]);
          }
          if (action !== "discard") burstReload();
          sessionMissions.reload();
        } catch (e) {
          setMessages((prev) => [...prev, { role: "assistant", text: "_(couldn't do that — please try again)_" }]);
          toast.error(humanizeError(e, "Couldn't send your reply"));
          sessionMissions.reload(); // a lost-response reply may have applied → re-sync
        } finally {
          planBusyRef.current = false;
          setReplyBusy(false);
        }
      },
      [awaitingMission, burstReload, projectId, sessionMissions, toast],
    );

    const send = useCallback(async (override?: string, forceChat = false) => {
      const text = (override ?? input).trim();
      if (!text || busy) return;
      // A plan is awaiting approval → the typed reply goes to the server, which reads
      // the intent with a small model and approves / revises / discards. Mission mode is
      // already off here (one-shot). forceChat (suggestion chips) opts out.
      if (awaitingMission && !forceChat) {
        if (planBusyRef.current) return; // guard against a double-Enter firing two replies
        setInput("");
        requestAnimationFrame(growComposer);
        void replyToPlan(text);
        return;
      }
      // Mission mode is ONE-SHOT: it arms THIS message to start a mission, then
      // disarms immediately. So follow-up replies — steering, "thanks", questions —
      // are ordinary chat and never spawn a second mission. (Re-armed below if the
      // send fails, so a retry is a single tap.) forceChat lets canned actions
      // (the suggestion chips) stay plain chat even while the toggle is armed.
      const asMission = missionMode && !forceChat;
      if (asMission) setMissionMode(false);
      setInput("");
      requestAnimationFrame(growComposer);
      setMessages((prev) => [...prev, { role: "user", text }]);
      resetDraft();
      setBusy(true);
      try {
        const r = await postAgentMessage(projectId, convRef.current, text, asMission ? "mission" : undefined);
        setStatus(r.status);
        persist(r.run_id);
        if (asMission) {
          // No chat turn to stream — the mission's ack + plan land via the sticky
          // dock + the idle transcript poll. Pull the ack in promptly too. The toast
          // confirms the launch (the toggle just disarmed itself) so the one-shot
          // switch-back doesn't feel like the click was lost.
          toast.success("Mission starting — I'll draft a plan for you to approve.");
          setBusy(false);
          const runId = r.run_id;
          window.setTimeout(() => {
            void getAgentRun(projectId, runId)
              .then((v) => setMessages((prev) => ((v.messages?.length ?? 0) > prev.length ? v.messages : prev)))
              .catch(() => {});
          }, 1500);
          return;
        }
        openStream(r.run_id);
      } catch (e) {
        setBusy(false);
        if (asMission) setMissionMode(true); // re-arm so retry stays a mission
        const msg =
          e instanceof APIError && e.status === 503
            ? "The agent runtime isn't configured on this deployment."
            : humanizeError(e, "Couldn't reach the agent");
        toast.error(msg);
        setMessages((prev) => [...prev, { role: "assistant", text: "_(failed to send — try again)_" }]);
      }
    }, [input, busy, planBusy, projectId, openStream, persist, resetDraft, toast, missionMode, awaitingMission, replyToPlan]);

    // Stable retry (via a ref so it doesn't depend on input/busy and thus never
    // breaks History's memo). Re-sends a prior user message — handy when a turn
    // failed (the agent runtime auto-retries transient errors, but this lets you
    // re-ask without retyping).
    const sendRef = useRef(send);
    sendRef.current = send;
    const retry = useCallback((t: string) => {
      void sendRef.current(t);
    }, []);

    // The file/view shown in the right-side viewer panel (workspace file clicked in
    // chat, or a generated view's card). null = panel closed, chat full width.
    const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
    const openFile = useCallback((f: WorkspaceFileRef) => setPanelItem({ kind: "file", file: f }), []);
    const openView = useCallback((f: AgentFileRef) => {
      if (f.url) setPanelItem({ kind: "view", url: f.url, name: f.name });
    }, []);

    // Stop the in-progress run (the Send→Stop toggle). Idempotent; the backend
    // ends the run cleanly ("(stopped)") and the stream's `done` clears busy.
    const stop = useCallback(() => {
      const runId = runIdRef.current;
      if (!runId) return;
      void cancelAgentRun(projectId, runId).catch(() => {
        /* the run is durable — the stream's done / safety poll reconcile it */
      });
    }, [projectId]);

    const newChat = useCallback(() => {
      esRef.current?.close();
      convRef.current = crypto.randomUUID();
      setConvId(convRef.current);
      runIdRef.current = "";
      try {
        localStorage.removeItem(storeKey);
      } catch {
        /* ignore */
      }
      setMessages([]);
      setPanelItem(null);
      resetDraft();
      setStatus("");
      setBusy(false);
      textareaRef.current?.focus();
    }, [storeKey, resetDraft]);

    // loadConversation switches to an existing conversation (from the history
    // list): point at its conv/run, persist, load the transcript, and resume the
    // live stream if it's still active. Mirrors the on-mount resume path.
    const loadConversation = useCallback(
      (conversationId: string, runId: string) => {
        esRef.current?.close();
        setMessages([]);
        setPanelItem(null);
        resetDraft();
        setStatus("");
        setBusy(false);
        // Verify the run loads BEFORE switching to it — a reaped/deleted run must
        // not leave localStorage pointed at a dead id with a blank panel.
        getAgentRun(projectId, runId)
          .then((v) => {
            convRef.current = conversationId;
            setConvId(conversationId);
            persist(runId);
            setMessages(v.messages ?? []);
            setStatus(v.status ?? "");
            if (isActive(v.status)) {
              setBusy(true);
              openStream(runId);
            }
            textareaRef.current?.focus();
          })
          .catch(() => {
            toast.error("That conversation is no longer available.");
            newChat(); // recover to a clean fresh chat instead of a dead run
          });
      },
      [projectId, persist, resetDraft, openStream, toast, newChat],
    );

    useImperativeHandle(ref, () => ({ newChat, loadConversation }), [newChat, loadConversation]);

    // Gate on !draft too: once visible reply text is on screen, a between-turn
    // pause must NOT collapse into a bare spinner under it (that was the
    // "text → spinner → text" half of the first-message flicker).
    const showThinking = busy && status !== "awaiting_human" && !streaming && !activeTool && !draft;
    // What the waiting dots say: "Thinking…" during a model turn (incl. a typed
    // reply / plan decision), and a general "Working…" otherwise — the start-up
    // "preparing" phase + the queued state both read "Working…" so a routine
    // message never says the jarring "Connecting…".
    const thinkingLabel = planBusy || replyBusy || phase === "thinking" ? "Thinking…" : "Working…";

    return (
      <div className="flex h-full min-h-0">
        <div
          className={cn(
            "flex min-w-0 flex-col",
            // When the viewer panel is open, squeeze the chat to a readable column
            // (≈40%, capped) so the preview gets the majority of the width; full
            // width otherwise. (Gated at sm+; on mobile the panel is a full overlay.)
            panelItem ? "flex-1 sm:w-2/5 sm:max-w-[560px] sm:flex-none" : "flex-1",
          )}
        >
          {/* Transcript */}
        <div
          ref={scrollRef}
          aria-live="polite"
          onScroll={(e) => {
            const el = e.currentTarget;
            pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          }}
          className="scrollbar-hide flex-1 overflow-auto"
        >
          <div className="mx-auto w-full max-w-3xl px-4 py-8">
            {messages.length === 0 && !draft && !busy && (
              <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
                {/* Channel arc — the agent (center) reachable across the chat, Telegram & Slack. */}
                <div className="relative mb-6 h-28 w-full max-w-sm">
                  <div className="absolute bottom-0 left-1/2 w-56 -translate-x-1/2 rounded-2xl border bg-card p-3 text-left shadow-md">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/15 text-primary">
                        <Sparkles className="h-3.5 w-3.5" />
                      </span>
                      <span className="truncate text-xs font-semibold">{agentName}</span>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      <div className="h-1.5 w-4/5 rounded-full bg-muted" />
                      <div className="h-1.5 w-3/5 rounded-full bg-muted" />
                    </div>
                  </div>
                  <ChannelBadge className="left-[8%] top-7">
                    <TelegramLogo />
                  </ChannelBadge>
                  <ChannelBadge className="left-1/2 top-0 -translate-x-1/2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                      <Sparkles className="h-4 w-4" />
                    </span>
                  </ChannelBadge>
                  <ChannelBadge className="right-[8%] top-7">
                    <SlackLogo />
                  </ChannelBadge>
                </div>
                <h2 className="text-3xl font-semibold text-foreground">Chat with {agentName}</h2>
                <p className="mt-2 max-w-md text-[15px] leading-relaxed text-muted-foreground">
                  Ask for anything you need. Your agent gets to work on it — and if it ever needs a person's help, it
                  pauses and picks right back up here the moment someone answers.
                </p>

                <div className="mt-8 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
                  <Capability
                    icon={ScrollText}
                    title="Knows your playbook"
                    desc="Follows the identity, voice, and boundaries you set for it."
                  />
                  <Capability
                    icon={Plug}
                    title="Uses your tools"
                    desc="Runs your connected integrations to get real work done."
                  />
                  <Capability
                    icon={Database}
                    title="Remembers"
                    desc="Keeps notes and context in its workspace across conversations."
                  />
                  <Capability
                    icon={MessagesSquare}
                    title="Reach it anywhere"
                    desc="Right here, or through your connected Telegram & Slack."
                  />
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                  {["What can you help me with?", "What tools do you have?", "Help me get started"].map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s, true)}
                      className="rounded-full border px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ViewOpenContext.Provider value={openView}>
              <FileLinkContext.Provider value={openFile}>
                <div className="space-y-6">
                <History messages={messages} projectId={projectId} onRetry={retry} />
                {draft && <Turn role="assistant" text={draft} streaming projectId={projectId} />}
                {activeTool && status !== "awaiting_human" && <ToolRunning name={activeTool} />}
                {(showThinking || planBusy || replyBusy) && <Thinking label={thinkingLabel} />}
                {status === "awaiting_human" && <WaitingPill projectId={projectId} />}
                {/* A finished mission's breakdown renders inline above its result
                    message (tagged by mission_id), persistent in history. Running ones
                    live in the dock above the input. A plan AWAITING approval reads as
                    a chat turn here — the agent presenting its plan; approve/revise
                    happen in the composer's plan bar. */}
                {sessionMissions.awaiting.map((mi) => (
                  <InlinePlanReview key={mi.id} projectId={projectId} mission={mi} />
                ))}
                {/* Live mission cards (running/paused/blocked) sit inline at the bottom
                    of the transcript — the session is about this mission, so its
                    progress reads as part of the chat, not a floating dock. */}
                <OngoingMissions projectId={projectId} m={sessionMissions} revisingId={revisingId} />
                </div>
              </FileLinkContext.Provider>
            </ViewOpenContext.Provider>
          </div>
        </div>

        {/* Composer */}
        <div className="px-4 pb-5 pt-1">
          <div className="mx-auto w-full max-w-3xl">
            <div
              className={cn(
                "overflow-hidden rounded-[26px] border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_28px_-16px_rgba(15,23,42,0.18)] transition-all duration-200",
                awaitingMission || missionMode
                  ? "border-primary/45 ring-4 ring-primary/10"
                  : "border-border/70 focus-within:border-primary/35 focus-within:ring-4 focus-within:ring-primary/10",
              )}
            >
              {/* Plan bar — the always-reachable actions for a plan awaiting approval.
                  The full plan itself reads as a chat card up in the transcript. */}
              {awaitingMission && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-primary/15 bg-primary/[0.04] px-3.5 py-2.5">
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(`mission-plan-${awaitingMission.id}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "center" })
                    }
                    title="Jump to the plan"
                    className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:underline"
                  >
                    <Target className="h-4 w-4 text-primary" /> Plan ready to approve
                    {(awaitingMission.tasks_total ?? 0) > 0 && (
                      <span className="text-muted-foreground">· {awaitingMission.tasks_total} steps</span>
                    )}
                  </button>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void decidePlan("discard", "Discard")}
                      disabled={planBusy || replyBusy}
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Discard
                    </button>
                    <button
                      type="button"
                      onClick={() => void decidePlan("approve", "Approve")}
                      disabled={planBusy || replyBusy}
                      className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                    >
                      {planBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve &amp; run
                    </button>
                  </div>
                </div>
              )}
              <textarea
                ref={textareaRef}
                className="block max-h-52 min-h-[28px] w-full resize-none bg-transparent px-5 pt-3.5 text-[15px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/70"
                rows={1}
                placeholder={
                  awaitingMission
                    ? "Reply to the plan — “go ahead”, “discard”, or what to change…"
                    : missionMode
                      ? `Describe a mission for ${agentName}…`
                      : `Message ${agentName}…`
                }
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  growComposer();
                }}
                onKeyDown={(e) => {
                  // Enter always SENDS the message (which revises the plan when one is
                  // pending). No Enter-to-approve shortcut — ⌘/Ctrl+Enter is muscle-memory
                  // for "send", so binding it to Approve would discard a typed revision.
                  // Approving is a deliberate click on the Approve button.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
              {/* Toolbar below the field — nothing overlaps the text. */}
              <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5 pt-2">
                {awaitingMission ? (
                  // While reviewing a plan, the composer revises it — no Mission toggle.
                  <span className="inline-flex select-none items-center gap-1.5 px-1 text-xs text-muted-foreground">
                    <Pencil className="h-3.5 w-3.5" /> Reply to approve, change, or discard the plan
                  </span>
                ) : (
                  /* Mission mode toggle (like plan mode): arms the NEXT message to start
                     a mission, then disarms — one-shot, so replies don't spawn missions. */
                  <button
                    type="button"
                    onClick={() => setMissionMode((v) => !v)}
                    aria-pressed={missionMode}
                    title={missionMode ? "Your next message will start a mission, then this switches back to chat" : "Make your next message start a mission"}
                    className={cn(
                      "inline-flex select-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.98]",
                      missionMode
                        ? "border-primary/30 bg-primary/10 text-primary shadow-sm"
                        : "border-border/70 text-muted-foreground hover:border-border hover:bg-muted/60 hover:text-foreground",
                    )}
                  >
                    <Target className="h-3.5 w-3.5" /> Mission
                  </button>
                )}
                {busy ? (
                  <button
                    type="button"
                    onClick={stop}
                    aria-label="Stop"
                    title="Stop"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background shadow-sm transition-all hover:opacity-90 active:scale-95"
                  >
                    <Square className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                  </button>
                ) : (
                  <button
                    onClick={() => void send()}
                    disabled={!input.trim()}
                    aria-label="Send"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-30 disabled:shadow-none"
                  >
                    <ArrowUp className="h-[18px] w-[18px]" />
                  </button>
                )}
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground/80">
              {awaitingMission
                ? "Reviewing the plan — use the buttons, or just reply (“go ahead”, or tell me what to change)."
                : missionMode
                  ? "Mission mode armed — your next message starts a mission (it plans, then asks you to approve), then switches back to chat."
                  : "Enter to send · Shift+Enter for a new line"}
            </p>
          </div>
        </div>
        </div>
        {panelItem && (
          <FileViewerPanel projectId={projectId} item={panelItem} onClose={() => setPanelItem(null)} />
        )}
      </div>
    );
  },
);
